import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from '../../../utils/hooks/makeStyles'
import { toast } from 'react-toastify'
import {
  CreateOththamologyApi,
  GetOththamologyApi,
} from '../../../services/CampManagementService'

interface OphthalmologyData {
  patientDetails: {
    observationId?: string
    name: string
    age: number
    sex: string
    campIdOrTokenNo: string
    date: string
    mobileNo: string
  }
  chiefComplaints: {
    observationId?: string
    diminishedVisionNear: boolean
    diminishedVisionDistance: boolean
    redness: boolean
    watering: boolean
    pain: boolean
    itchingBurning: boolean
    headacheEyeStrain: boolean
    suddenVisionLoss: boolean
    nightVisionProblem: boolean
    foreignBodySensation: boolean
    asymptomatic: boolean
    duration: string
  }
  history: {
    observationId?: string
    diabetes: boolean
    hypertension: boolean
    glaucoma: boolean
    thyroidDisorder: boolean
    previousEyeSurgery: boolean
    trauma: boolean
    longTermSteroidUse: boolean
    familyHistoryEyeDisease: boolean
    nilSignificant: boolean
  }
  visualAcuity: {
    observationId?: string
    rightDistance: string
    rightNear: string
    leftDistance: string
    leftNear: string
    unaided: boolean
    withGlasses: boolean
    pinhole: boolean
  }
  refraction: {
    observationId?: string
    OD: string
    OS: string
  }
  intraocularPressure: {
    observationId?: string
    OD: string
    OS: string
    method: 'NCT' | 'Applanation' | ''
  }
  anteriorSegment: {
    observationId?: string
    normal: boolean
    conjunctivalCongestion: boolean
    cornealOpacityUlcer: boolean
    shallowAC: boolean
    cataract: boolean
  }
  fundusExamination: {
    observationId?: string
    notDone: boolean
    normal: boolean
    diabeticRetinopathy: boolean
    glaucomaSuspect: boolean
    hypertensiveRetinopathy: boolean
    macularPathology: boolean
    mediaHaze: boolean
    dilated: 'yes' | 'no' | ''
  }
  provisionalDiagnosis: {
    observationId?: string
    refractiveError: boolean
    cataract: boolean
    dryEye: boolean
    conjunctivitis: boolean
    glaucomaSuspect: boolean
    diabeticEyeDisease: boolean
    other: string
  }
  advicePlan: {
    observationId?: string
    spectacles: boolean
    eyeDrops: boolean
    furtherEvaluation: boolean
    fundusPhotoOCT: boolean
    referralToHigherCenter: boolean
    surgeryAdvised: boolean
    reviewAfterDays: string
  }
  doctorDetails: {
    observationId?: string
    name: string
    regNo: string
  }
  barcodeId1: string
  barcodeId2: string
  barcodeId3: string
}

const useStyles = makeStyles(() => ({
  container: {
    padding: 24,
  },
  section: {
    marginBottom: 24,
    marginTop: 24,
  },
  sectionHeader: {
    marginBottom: 16,
    fontWeight: 600,
    color: '#1976d2',
  },
  divider: {
    marginTop: 32,
    marginBottom: 32,
  },
  firstSection: {
    marginBottom: 16,
    marginTop: 24,
  },
  durationContainer: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  durationSelect: {
    minWidth: 150,
  },
  otherInput: {
    minWidth: 200,
  },
  reviewInput: {
    width: 120,
  },
  tableInput: {
    width: 100,
  },
  submitWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  inlineGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 8,
  },
}))

function OpthmologyScreening({ appointmentData }: any) {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)

  // Calculate age from date of birth if needed, or use default
  // const calculateAge = (dob: string) => {
  //   if (!dob) return 0
  //   const today = new Date()
  //   const birthDate = new Date(dob)
  //   let age = today.getFullYear() - birthDate.getFullYear()
  //   const monthDiff = today.getMonth() - birthDate.getMonth()
  //   if (
  //     monthDiff < 0 ||
  //     (monthDiff === 0 && today.getDate() < birthDate.getDate())
  //   ) {
  //     age--
  //   }
  //   return age
  // }

  const [data, setData] = useState<any>({
    patientDetails: {
      name: appointmentData?.name || '',
      age: appointmentData?.age || 0,
      sex: appointmentData?.gender || '',
      campIdOrTokenNo: appointmentData?.campId || '',
      date: new Date()?.toISOString()?.split('T')[0],
      mobileNo: appointmentData?.mobile || '',
    },
    chiefComplaints: {
      diminishedVisionNear: false,
      diminishedVisionDistance: false,
      redness: false,
      watering: false,
      pain: false,
      itchingBurning: false,
      headacheEyeStrain: false,
      suddenVisionLoss: false,
      nightVisionProblem: false,
      foreignBodySensation: false,
      asymptomatic: false,
      duration: '',
    },
    history: {
      diabetes: false,
      hypertension: false,
      glaucoma: false,
      thyroidDisorder: false,
      previousEyeSurgery: false,
      trauma: false,
      longTermSteroidUse: false,
      familyHistoryEyeDisease: false,
      nilSignificant: false,
    },
    visualAcuity: {
      rightDistance: '',
      rightNear: '',
      leftDistance: '',
      leftNear: '',
      unaided: false,
      withGlasses: false,
      pinhole: false,
    },
    refraction: {
      OD: '',
      OS: '',
    },
    intraocularPressure: {
      OD: '',
      OS: '',
      method: '',
    },
    anteriorSegment: {
      normal: false,
      conjunctivalCongestion: false,
      cornealOpacityUlcer: false,
      shallowAC: false,
      cataract: false,
    },
    fundusExamination: {
      notDone: false,
      normal: false,
      diabeticRetinopathy: false,
      glaucomaSuspect: false,
      hypertensiveRetinopathy: false,
      macularPathology: false,
      mediaHaze: false,
      dilated: '',
    },
    provisionalDiagnosis: {
      refractiveError: false,
      cataract: false,
      dryEye: false,
      conjunctivitis: false,
      glaucomaSuspect: false,
      diabeticEyeDisease: false,
      other: '',
    },
    advicePlan: {
      spectacles: false,
      eyeDrops: false,
      furtherEvaluation: false,
      fundusPhotoOCT: false,
      referralToHigherCenter: false,
      surgeryAdvised: false,
      reviewAfterDays: '',
    },
    doctorDetails: {
      name: appointmentData?.visitingDoctorName || '',
      regNo: appointmentData?.visitingDoctorId || '',
    },
    barcodeId1: '',
    barcodeId2: '',
    barcodeId3: '',
  })

  // Fetch existing Ophthalmology data on component mount
  useEffect(() => {
    fetchOphthalmologyData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentData?.encounterId])

  const fetchOphthalmologyData = async () => {
    if (appointmentData?.encounterId) {
      try {
        const response = await GetOththamologyApi(appointmentData?.encounterId)

        if (response?.success && response?.data) {
          // Map response data to state
          const ophData = response?.data
          setData((prevData: any) => {
            // Convert dilatedYes boolean to dilated string value
            const fundusExam = ophData?.fundusExamination
              ? {
                  ...ophData.fundusExamination,
                  dilated:
                    ophData.fundusExamination.dilatedYes === true
                      ? 'yes'
                      : ophData.fundusExamination.dilatedYes === false
                      ? 'no'
                      : '',
                }
              : prevData?.fundusExamination

            // Convert nct/applanation booleans to method string value
            const intraocularPressure = ophData?.intraocularPressure
              ? {
                  ...ophData.intraocularPressure,
                  method:
                    ophData.intraocularPressure.nct === true
                      ? 'NCT'
                      : ophData.intraocularPressure.applanation === true
                      ? 'Applanation'
                      : '',
                }
              : prevData?.intraocularPressure

            return {
              patientDetails:
                ophData?.patientDetails || prevData?.patientDetails,
              chiefComplaints:
                ophData?.chiefComplaints || prevData?.chiefComplaints,
              history: ophData?.relevantHistory || prevData?.history,
              visualAcuity: ophData?.visualAcuity || prevData?.visualAcuity,
              refraction: ophData?.refraction || prevData?.refraction,
              intraocularPressure: intraocularPressure,
              anteriorSegment:
                ophData?.anteriorSegment || prevData?.anteriorSegment,
              fundusExamination: fundusExam,
              provisionalDiagnosis:
                ophData?.impressionDiagnosis || prevData?.provisionalDiagnosis,
              advicePlan: ophData?.advicePlan || prevData?.advicePlan,
              doctorDetails: ophData?.doctorDetails || prevData?.doctorDetails,
              barcodeId1: ophData?.barcodeId1 || prevData?.barcodeId1,
              barcodeId2: ophData?.barcodeId2 || prevData?.barcodeId2,
              barcodeId3: ophData?.barcodeId3 || prevData?.barcodeId3,
            }
          })
        }
      } catch (error) {
        console.error('Error fetching ophthalmology data:', error)
      }
    }
  }

  const handleChiefComplaintChange =
    (field: keyof OphthalmologyData['chiefComplaints']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        chiefComplaints: {
          ...data?.chiefComplaints,
          [field]: event?.target?.checked,
        },
      })
    }

  // const handleDurationChange = (event: any) => {
  //   setData({
  //     ...data,
  //     chiefComplaints: {
  //       ...data?.chiefComplaints,
  //       duration: event?.target?.value,
  //     },
  //   })
  // }

  const handleHistoryChange =
    (field: keyof OphthalmologyData['history']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        history: { ...data?.history, [field]: event?.target?.checked },
      })
    }

  const handleVisualAcuityChange =
    (field: keyof OphthalmologyData['visualAcuity']) => (event: any) => {
      const value =
        event?.target?.type === 'checkbox'
          ? event?.target?.checked
          : event?.target?.value
      setData({
        ...data,
        visualAcuity: { ...data?.visualAcuity, [field]: value },
      })
    }

  const handleRefractionChange =
    (field: keyof OphthalmologyData['refraction']) => (event: any) => {
      setData({
        ...data,
        refraction: { ...data?.refraction, [field]: event?.target?.value },
      })
    }

  const handleIOPChange =
    (field: keyof OphthalmologyData['intraocularPressure']) => (event: any) => {
      setData({
        ...data,
        intraocularPressure: {
          ...data?.intraocularPressure,
          [field]: event?.target?.value,
        },
      })
    }

  const handleAnteriorSegmentChange =
    (field: keyof OphthalmologyData['anteriorSegment']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        anteriorSegment: {
          ...data?.anteriorSegment,
          [field]: event?.target?.checked,
        },
      })
    }

  const handleFundusChange =
    (field: keyof OphthalmologyData['fundusExamination']) => (event: any) => {
      const value =
        event?.target?.type === 'checkbox'
          ? event?.target?.checked
          : event?.target?.value
      setData({
        ...data,
        fundusExamination: { ...data?.fundusExamination, [field]: value },
      })
    }

  const handleDiagnosisChange =
    (field: keyof OphthalmologyData['provisionalDiagnosis']) =>
    (event: any) => {
      const value =
        event?.target?.type === 'checkbox'
          ? event?.target?.checked
          : event?.target?.value
      setData({
        ...data,
        provisionalDiagnosis: { ...data?.provisionalDiagnosis, [field]: value },
      })
    }

  const handleAdviceChange =
    (field: keyof OphthalmologyData['advicePlan']) => (event: any) => {
      const value =
        event?.target?.type === 'checkbox'
          ? event?.target?.checked
          : event?.target?.value
      setData({
        ...data,
        advicePlan: { ...data?.advicePlan, [field]: value },
      })
    }

  const handleSubmit = async () => {
    setLoading(true)

    try {
      // Transform payload to match API structure
      const payload: any = {
        patientDetails: {
          ...(data?.patientDetails?.observationId && {
            observationId: data?.patientDetails?.observationId,
          }),
          name: data?.patientDetails?.name,
          age: data?.patientDetails?.age,
          sex: data?.patientDetails?.sex?.toUpperCase().startsWith('M')
            ? 'M'
            : 'F',
          campIdOrTokenNo: data?.patientDetails?.campIdOrTokenNo,
          date: data?.patientDetails?.date,
          mobileNo: data?.patientDetails?.mobileNo,
        },
        chiefComplaints: {
          ...(data?.chiefComplaints?.observationId && {
            observationId: data?.chiefComplaints?.observationId,
          }),
          diminishedVisionNear: data?.chiefComplaints?.diminishedVisionNear,
          diminishedVisionDistance:
            data?.chiefComplaints?.diminishedVisionDistance,
          redness: data?.chiefComplaints?.redness,
          watering: data?.chiefComplaints?.watering,
          pain: data?.chiefComplaints?.pain,
          itchingBurning: data?.chiefComplaints?.itchingBurning,
          headacheEyeStrain: data?.chiefComplaints?.headacheEyeStrain,
          suddenVisionLoss: data?.chiefComplaints?.suddenVisionLoss,
          nightVisionProblem: data?.chiefComplaints?.nightVisionProblem,
          foreignBodySensation: data?.chiefComplaints?.foreignBodySensation,
          asymptomatic: data?.chiefComplaints?.asymptomatic,
          duration: data?.chiefComplaints?.duration,
        },
        relevantHistory: {
          ...(data?.history?.observationId && {
            observationId: data?.history?.observationId,
          }),
          diabetes: data?.history?.diabetes,
          hypertension: data?.history?.hypertension,
          glaucoma: data?.history?.glaucoma,
          thyroidDisorder: data?.history?.thyroidDisorder,
          previousEyeSurgery: data?.history?.previousEyeSurgery,
          trauma: data?.history?.trauma,
          longTermSteroidUse: data?.history?.longTermSteroidUse,
          familyHistoryEyeDisease: data?.history?.familyHistoryEyeDisease,
          nilSignificant: data?.history?.nilSignificant,
        },
        visualAcuity: {
          ...(data?.visualAcuity?.observationId && {
            observationId: data?.visualAcuity?.observationId,
          }),
          rightDistance: data?.visualAcuity?.rightDistance,
          rightNear: data?.visualAcuity?.rightNear,
          leftDistance: data?.visualAcuity?.leftDistance,
          leftNear: data?.visualAcuity?.leftNear,
          unaided: data?.visualAcuity?.unaided,
          withGlasses: data?.visualAcuity?.withGlasses,
          pinhole: data?.visualAcuity?.pinhole,
        },
        refraction: {
          ...(data?.refraction?.observationId && {
            observationId: data?.refraction?.observationId,
          }),
          od: data?.refraction?.OD,
          os: data?.refraction?.OS,
        },
        intraocularPressure: {
          ...(data?.intraocularPressure?.observationId && {
            observationId: data?.intraocularPressure?.observationId,
          }),
          odMmHg: parseFloat(data?.intraocularPressure?.OD) || 0,
          osMmHg: parseFloat(data?.intraocularPressure?.OS) || 0,
          nct: data?.intraocularPressure?.method === 'NCT',
          applanation: data?.intraocularPressure?.method === 'Applanation',
        },
        anteriorSegment: {
          ...(data?.anteriorSegment?.observationId && {
            observationId: data?.anteriorSegment?.observationId,
          }),
          normal: data?.anteriorSegment?.normal,
          conjunctivalCongestion: data?.anteriorSegment?.conjunctivalCongestion,
          cornealOpacityUlcer: data?.anteriorSegment?.cornealOpacityUlcer,
          shallowAC: data?.anteriorSegment?.shallowAC,
          cataract: data?.anteriorSegment?.cataract,
        },
        fundusExamination: {
          ...(data?.fundusExamination?.observationId && {
            observationId: data?.fundusExamination?.observationId,
          }),
          notDone: data?.fundusExamination?.notDone,
          normal: data?.fundusExamination?.normal,
          diabeticRetinopathy: data?.fundusExamination?.diabeticRetinopathy,
          glaucomaSuspect: data?.fundusExamination?.glaucomaSuspect,
          hypertensiveRetinopathy:
            data?.fundusExamination?.hypertensiveRetinopathy,
          macularPathology: data?.fundusExamination?.macularPathology,
          mediaHaze: data?.fundusExamination?.mediaHaze,
          dilatedYes: data?.fundusExamination?.dilated === 'yes',
        },
        impressionDiagnosis: {
          ...(data?.provisionalDiagnosis?.observationId && {
            observationId: data?.provisionalDiagnosis?.observationId,
          }),
          refractiveError: data?.provisionalDiagnosis?.refractiveError,
          cataract: data?.provisionalDiagnosis?.cataract,
          dryEye: data?.provisionalDiagnosis?.dryEye,
          conjunctivitis: data?.provisionalDiagnosis?.conjunctivitis,
          glaucomaSuspect: data?.provisionalDiagnosis?.glaucomaSuspect,
          diabeticEyeDisease: data?.provisionalDiagnosis?.diabeticEyeDisease,
          other: data?.provisionalDiagnosis?.other,
        },
        advicePlan: {
          ...(data?.advicePlan?.observationId && {
            observationId: data?.advicePlan?.observationId,
          }),
          spectacles: data?.advicePlan?.spectacles,
          eyeDrops: data?.advicePlan?.eyeDrops,
          furtherEvaluation: data?.advicePlan?.furtherEvaluation,
          fundusPhotoOct: data?.advicePlan?.fundusPhotoOCT,
          referralHigherCenter: data?.advicePlan?.referralToHigherCenter,
          surgeryAdvised: data?.advicePlan?.surgeryAdvised,
          reviewAfterDays: parseInt(data?.advicePlan?.reviewAfterDays) || 0,
        },
        doctorDetails: {
          ...(data?.doctorDetails?.observationId && {
            observationId: data?.doctorDetails?.observationId,
          }),
          name: data?.doctorDetails?.name,
          regNo: data?.doctorDetails?.regNo,
        },
      }

      const response: any = await CreateOththamologyApi(
        payload,
        appointmentData?.visitId,
        appointmentData?.fhirId,
        appointmentData?.visitingDoctorId
      )

      if (response?.success) {
        toast.success('Ophthalmology screening submitted successfully')
        // Optionally reset form or navigate away
        fetchOphthalmologyData()
      } else {
        toast.error(response?.message || 'Failed to submit screening')
      }
    } catch (error: any) {
      console.error('Error submitting ophthalmology screening:', error)
      toast.error(error?.message || 'An error occurred while submitting')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box className={classes.container}>
      <Typography variant='h5' className={classes.sectionHeader}>
        Chief Complaints (Tick ✔)
      </Typography>

      {/* Chief Complaints */}
      <Box className={classes.firstSection}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.diminishedVisionNear}
                onChange={handleChiefComplaintChange('diminishedVisionNear')}
              />
            }
            label='Diminished vision (Near/ Distance)'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.redness}
                onChange={handleChiefComplaintChange('redness')}
              />
            }
            label='Redness'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.watering}
                onChange={handleChiefComplaintChange('watering')}
              />
            }
            label='Watering'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.pain}
                onChange={handleChiefComplaintChange('pain')}
              />
            }
            label='Pain'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.itchingBurning}
                onChange={handleChiefComplaintChange('itchingBurning')}
              />
            }
            label='Itching / Burning'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.headacheEyeStrain}
                onChange={handleChiefComplaintChange('headacheEyeStrain')}
              />
            }
            label='Headache / Eye strain'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.suddenVisionLoss}
                onChange={handleChiefComplaintChange('suddenVisionLoss')}
              />
            }
            label='Sudden vision loss'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.nightVisionProblem}
                onChange={handleChiefComplaintChange('nightVisionProblem')}
              />
            }
            label='Night vision problem'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.foreignBodySensation}
                onChange={handleChiefComplaintChange('foreignBodySensation')}
              />
            }
            label='Foreign body sensation'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.chiefComplaints?.asymptomatic}
                onChange={handleChiefComplaintChange('asymptomatic')}
              />
            }
            label='Asymptomatic'
          />
        </FormGroup>

        {/* <Box className={classes.durationContainer}>
          <Typography variant='body1'>Duration:</Typography>
          <Select
            value={data?.chiefComplaints?.duration}
            onChange={handleDurationChange}
            displayEmpty
            className={classes.durationSelect}
          >
            <MenuItem value=''>
              <em>Select duration</em>
            </MenuItem>
            <MenuItem value='12hrs'>12 hrs</MenuItem>
            <MenuItem value='24hrs'>24 hrs</MenuItem>
            <MenuItem value='48hrs'>48 hrs</MenuItem>
          </Select>
        </Box> */}
      </Box>

      <Divider className={classes.divider} />

      {/* Relevant History */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Relevant History (Tick ✔)
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.diabetes}
                onChange={handleHistoryChange('diabetes')}
              />
            }
            label='Diabetes'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.hypertension}
                onChange={handleHistoryChange('hypertension')}
              />
            }
            label='Hypertension'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.glaucoma}
                onChange={handleHistoryChange('glaucoma')}
              />
            }
            label='Glaucoma'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.thyroidDisorder}
                onChange={handleHistoryChange('thyroidDisorder')}
              />
            }
            label='Thyroid disorder'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.previousEyeSurgery}
                onChange={handleHistoryChange('previousEyeSurgery')}
              />
            }
            label='Previous eye surgery'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.trauma}
                onChange={handleHistoryChange('trauma')}
              />
            }
            label='Trauma'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.longTermSteroidUse}
                onChange={handleHistoryChange('longTermSteroidUse')}
              />
            }
            label='Long-term steroid use'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.familyHistoryEyeDisease}
                onChange={handleHistoryChange('familyHistoryEyeDisease')}
              />
            }
            label='Family history of eye disease'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.history?.nilSignificant}
                onChange={handleHistoryChange('nilSignificant')}
              />
            }
            label='Nil significant'
          />
        </FormGroup>
      </Box>

      <Divider className={classes.divider} />

      {/* Visual Acuity */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Visual Acuity
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Eye</TableCell>
                <TableCell>Distance</TableCell>
                <TableCell>Near</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Right (OD)</TableCell>
                <TableCell>
                  <TextField
                    value={data?.visualAcuity?.rightDistance}
                    onChange={handleVisualAcuityChange('rightDistance')}
                    placeholder='____'
                    className={classes.tableInput}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={data?.visualAcuity?.rightNear}
                    onChange={handleVisualAcuityChange('rightNear')}
                    placeholder='____'
                    className={classes.tableInput}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Left (OS)</TableCell>
                <TableCell>
                  <TextField
                    value={data?.visualAcuity?.leftDistance}
                    onChange={handleVisualAcuityChange('leftDistance')}
                    placeholder='____'
                    className={classes.tableInput}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={data?.visualAcuity?.leftNear}
                    onChange={handleVisualAcuityChange('leftNear')}
                    placeholder='____'
                    className={classes.tableInput}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <FormGroup row style={{ marginTop: 8 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.visualAcuity?.unaided}
                onChange={handleVisualAcuityChange('unaided')}
              />
            }
            label='Unaided'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.visualAcuity?.withGlasses}
                onChange={handleVisualAcuityChange('withGlasses')}
              />
            }
            label='With glasses'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.visualAcuity?.pinhole}
                onChange={handleVisualAcuityChange('pinhole')}
              />
            }
            label='Pinhole'
          />
        </FormGroup>
      </Box>

      <Divider className={classes.divider} />

      {/* Refraction */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Refraction (if required)
        </Typography>
        <Box className={classes.inlineGroup}>
          <Typography variant='body1'>OD:</Typography>
          <TextField
            value={data?.refraction?.OD || data?.refraction?.od}
            onChange={handleRefractionChange('OD')}
            placeholder='___________________'
            style={{ minWidth: 200 }}
          />
        </Box>
        <Box className={classes.inlineGroup}>
          <Typography variant='body1'>OS:</Typography>
          <TextField
            value={data?.refraction?.OS || data?.refraction?.os}
            onChange={handleRefractionChange('OS')}
            placeholder='___________________'
            style={{ minWidth: 200 }}
          />
        </Box>
      </Box>

      <Divider className={classes.divider} />

      {/* Intraocular Pressure */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Intraocular Pressure
        </Typography>
        <Box className={classes.inlineGroup}>
          <Typography variant='body1'>OD:</Typography>
          <TextField
            value={
              data?.intraocularPressure?.OD ?? data?.intraocularPressure?.odMmHg
            }
            onChange={handleIOPChange('OD')}
            placeholder='____'
            style={{ width: 80 }}
          />
          <Typography variant='body1'>mmHg</Typography>
          <Typography variant='body1'>OS:</Typography>
          <TextField
            value={
              data?.intraocularPressure?.OS ?? data?.intraocularPressure?.osMmHg
            }
            onChange={handleIOPChange('OS')}
            placeholder='____'
            style={{ width: 80 }}
          />
          <Typography variant='body1'>mmHg</Typography>
        </Box>
        <RadioGroup
          row
          value={data?.intraocularPressure?.method || ''}
          onChange={handleIOPChange('method')}
          style={{ marginTop: 8 }}
        >
          <FormControlLabel
            value='NCT'
            control={
              <Radio checked={data?.intraocularPressure?.method === 'NCT'} />
            }
            label='NCT'
          />
          <FormControlLabel
            value='Applanation'
            control={
              <Radio
                checked={data?.intraocularPressure?.method === 'Applanation'}
              />
            }
            label='Applanation'
          />
        </RadioGroup>
      </Box>

      <Divider className={classes.divider} />

      {/* Anterior Segment */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Anterior Segment (Torch / Slit Lamp)
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.anteriorSegment?.normal}
                onChange={handleAnteriorSegmentChange('normal')}
              />
            }
            label='Normal'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.anteriorSegment?.conjunctivalCongestion}
                onChange={handleAnteriorSegmentChange('conjunctivalCongestion')}
              />
            }
            label='Conjunctival congestion'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.anteriorSegment?.cornealOpacityUlcer}
                onChange={handleAnteriorSegmentChange('cornealOpacityUlcer')}
              />
            }
            label='Corneal opacity / ulcer'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.anteriorSegment?.shallowAC}
                onChange={handleAnteriorSegmentChange('shallowAC')}
              />
            }
            label='Shallow AC'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.anteriorSegment?.cataract}
                onChange={handleAnteriorSegmentChange('cataract')}
              />
            }
            label='Cataract'
          />
        </FormGroup>
      </Box>

      <Divider className={classes.divider} />

      {/* Fundus Examination */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Fundus Examination
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.notDone}
                onChange={handleFundusChange('notDone')}
              />
            }
            label='Not done'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.normal}
                onChange={handleFundusChange('normal')}
              />
            }
            label='Normal'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.diabeticRetinopathy}
                onChange={handleFundusChange('diabeticRetinopathy')}
              />
            }
            label='Diabetic retinopathy'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.glaucomaSuspect}
                onChange={handleFundusChange('glaucomaSuspect')}
              />
            }
            label='Glaucoma suspect'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.hypertensiveRetinopathy}
                onChange={handleFundusChange('hypertensiveRetinopathy')}
              />
            }
            label='Hypertensive retinopathy'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.macularPathology}
                onChange={handleFundusChange('macularPathology')}
              />
            }
            label='Macular pathology'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.fundusExamination?.mediaHaze}
                onChange={handleFundusChange('mediaHaze')}
              />
            }
            label='Media haze'
          />
        </FormGroup>
        <Box className={classes.inlineGroup}>
          <Typography variant='body1'>(Dilated</Typography>
          <RadioGroup
            row
            value={data?.fundusExamination?.dilated || ''}
            onChange={handleFundusChange('dilated')}
          >
            <FormControlLabel
              value='yes'
              control={
                <Radio checked={data?.fundusExamination?.dilated === 'yes'} />
              }
              label='Yes'
            />
            <FormControlLabel
              value='no'
              control={
                <Radio checked={data?.fundusExamination?.dilated === 'no'} />
              }
              label='No'
            />
          </RadioGroup>
          <Typography variant='body1'>)</Typography>
        </Box>
      </Box>

      <Divider className={classes.divider} />

      {/* Impression / Provisional Diagnosis */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Impression / Provisional Diagnosis
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.provisionalDiagnosis?.refractiveError}
                onChange={handleDiagnosisChange('refractiveError')}
              />
            }
            label='Refractive error'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.provisionalDiagnosis?.cataract}
                onChange={handleDiagnosisChange('cataract')}
              />
            }
            label='Cataract'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.provisionalDiagnosis?.dryEye}
                onChange={handleDiagnosisChange('dryEye')}
              />
            }
            label='Dry eye'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.provisionalDiagnosis?.conjunctivitis}
                onChange={handleDiagnosisChange('conjunctivitis')}
              />
            }
            label='Conjunctivitis'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.provisionalDiagnosis?.glaucomaSuspect}
                onChange={handleDiagnosisChange('glaucomaSuspect')}
              />
            }
            label='Glaucoma suspect'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.provisionalDiagnosis?.diabeticEyeDisease}
                onChange={handleDiagnosisChange('diabeticEyeDisease')}
              />
            }
            label='Diabetic eye disease'
          />
        </FormGroup>
        <Box className={classes.inlineGroup}>
          <Typography variant='body1'>Other:</Typography>
          <TextField
            value={data?.provisionalDiagnosis?.other}
            onChange={handleDiagnosisChange('other')}
            placeholder='Specify other'
            className={classes.otherInput}
          />
        </Box>
        <FormGroup row></FormGroup>
      </Box>

      <Divider className={classes.divider} />

      {/* Advice & Plan */}
      <Box className={classes.section}>
        <Typography variant='h6' className={classes.sectionHeader}>
          Advice & Plan
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.advicePlan?.spectacles}
                onChange={handleAdviceChange('spectacles')}
              />
            }
            label='Spectacles'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.advicePlan?.eyeDrops}
                onChange={handleAdviceChange('eyeDrops')}
              />
            }
            label='Eye drops'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.advicePlan?.furtherEvaluation}
                onChange={handleAdviceChange('furtherEvaluation')}
              />
            }
            label='Further evaluation'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.advicePlan?.fundusPhotoOCT}
                onChange={handleAdviceChange('fundusPhotoOCT')}
              />
            }
            label='Fundus photo / OCT'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.advicePlan?.referralToHigherCenter}
                onChange={handleAdviceChange('referralToHigherCenter')}
              />
            }
            label='Referral to higher center'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data?.advicePlan?.surgeryAdvised}
                onChange={handleAdviceChange('surgeryAdvised')}
              />
            }
            label='Surgery advised'
          />
        </FormGroup>
        <Box className={classes.inlineGroup}>
          <Typography variant='body1'>Review after</Typography>
          <TextField
            value={data?.advicePlan?.reviewAfterDays}
            onChange={handleAdviceChange('reviewAfterDays')}
            placeholder='__ days'
            type='number'
            className={classes.reviewInput}
          />
          <Typography variant='body1'>days</Typography>
        </Box>
      </Box>

      <Divider className={classes.divider} />

      {/* Submit Button */}
      <Box className={classes.submitWrapper}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>
    </Box>
  )
}

export default OpthmologyScreening
